import { useState, useEffect, ChangeEvent } from 'react';
import { useAuth, User } from '../context/AuthContext';
import { setupTotp, verifyTotpSetup, disableTotp, checkTotpEnabled } from '../lib/api';
import { ShieldCheck, Copy, Check, Warning } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

interface TotpData {
  secret: string;
  qr_code_url?: string;
}

export function TotpSettings() {
  const { user, updateUser } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState<'start' | 'qr' | 'verify'>('start');
  const [totpData, setTotpData] = useState<TotpData | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(user?.totp_enabled || false);

  // Check TOTP status from CommHub on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (user?.email) {
        try {
          const status = await checkTotpEnabled(user.email);
          setTotpEnabled(status.totp_enabled);
        } catch (e) {
          console.error('Failed to check TOTP status:', e);
        }
      }
    };
    checkStatus();
  }, [user?.email]);

  const handleSetupTotp = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Setting up TOTP with token:', token ? `${token.substring(0, 50)}...` : 'missing');
      console.log('Token appears to be JWT:', token?.includes('.') ? 'YES' : 'NO (probably dev token)');
      
      if (!token) {
        toast.error('Du måste vara inloggad för att konfigurera TOTP');
        return;
      }
      
      // Check if this is a real JWT (has 3 parts separated by dots)
      const isRealJwt = token.split('.').length === 3;
      if (!isRealJwt) {
        toast.error('TOTP-konfiguration kräver riktig inloggning (inte DEV-login). Logga in med e-post eller SMS först.');
        setDialogOpen(false);
        return;
      }
      
      const response = await setupTotp(token);
      console.log('TOTP setup response:', response);
      console.log('TOTP setup data:', response.data);
      setTotpData(response.data);
      setStep('qr');
    } catch (error: unknown) {
      console.error('Failed to setup TOTP:', error);
      const axiosError = error as { response?: { data?: unknown; status?: number } };
      if (axiosError.response) {
        console.error('Response data:', axiosError.response.data);
        console.error('Response status:', axiosError.response.status);
        
        if (axiosError.response.status === 401) {
          toast.error('Autentiseringsfel. Logga ut och logga in igen med e-post eller SMS.');
        } else {
          toast.error('Kunde inte starta TOTP-konfiguration');
        }
      } else {
        toast.error('Kunde inte starta TOTP-konfiguration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Ange en 6-siffrig kod');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await verifyTotpSetup(token!, verifyCode);
      toast.success('Autentiseringsapp aktiverad!');
      setTotpEnabled(true);
      if (user) {
        updateUser({ ...user, totp_enabled: true } as User);
      }
      setDialogOpen(false);
      resetState();
    } catch (error) {
      console.error('Failed to verify TOTP:', error);
      toast.error('Ogiltig kod. Försök igen.');
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTotp = async () => {
    if (!window.confirm('Är du säker på att du vill inaktivera autentiseringsappen?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await disableTotp(token!);
      toast.success('Autentiseringsapp inaktiverad');
      setTotpEnabled(false);
      if (user) {
        updateUser({ ...user, totp_enabled: false } as User);
      }
    } catch (error) {
      console.error('Failed to disable TOTP:', error);
      toast.error('Kunde inte inaktivera autentiseringsappen');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (totpData?.secret) {
      navigator.clipboard.writeText(totpData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Kopierad till urklipp');
    }
  };

  const resetState = () => {
    setStep('start');
    setTotpData(null);
    setVerifyCode('');
    setCopied(false);
  };

  return (
    <div className="pt-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-slate-600" />
            <span className="font-medium text-slate-900">Autentiseringsapp (TOTP)</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Använd Google Authenticator, Authy eller liknande app för extra säkerhet
          </p>
        </div>
        
        {totpEnabled ? (
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium">
              Aktiverad
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisableTotp}
              disabled={loading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Inaktivera
            </Button>
          </div>
        ) : (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetState();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                Aktivera
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck size={20} />
                  Konfigurera autentiseringsapp
                </DialogTitle>
              </DialogHeader>
              
              <div className="mt-4">
                {step === 'start' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      En autentiseringsapp genererar tidsbegränsade koder som du kan använda 
                      för att logga in istället för e-post eller SMS.
                    </p>
                    
                    <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">1</div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">Ladda ner en app</p>
                          <p className="text-xs text-slate-500">Google Authenticator, Authy, eller Microsoft Authenticator</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">2</div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">Skanna QR-koden</p>
                          <p className="text-xs text-slate-500">Eller ange den hemliga nyckeln manuellt</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">3</div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">Verifiera med en kod</p>
                          <p className="text-xs text-slate-500">Ange koden från appen för att aktivera</p>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleSetupTotp}
                      disabled={loading}
                      className="w-full"
                    >
                      {loading ? 'Laddar...' : 'Fortsätt'}
                    </Button>
                  </div>
                )}
                
                {step === 'qr' && totpData && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-4">
                        Skanna QR-koden med din autentiseringsapp
                      </p>
                      
                      {/* Generate QR code from otpauth URL or secret */}
                      <div className="flex justify-center mb-4">
                        <div className="p-4 bg-white border-2 border-slate-200 rounded-lg inline-block">
                          {totpData.qr_code_url ? (
                            // If we have an otpauth:// URL, generate QR from it
                            <QRCodeSVG 
                              value={totpData.qr_code_url}
                              size={192}
                              level="M"
                              includeMargin={false}
                            />
                          ) : totpData.secret ? (
                            // Generate otpauth URL from secret if not provided
                            <QRCodeSVG 
                              value={`otpauth://totp/Förvaltningen:${user?.email || 'user'}?secret=${totpData.secret}&issuer=Förvaltningen`}
                              size={192}
                              level="M"
                              includeMargin={false}
                            />
                          ) : (
                            <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center">
                              <span className="text-slate-400">Ingen QR-kod</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs text-slate-500 mb-2">
                          Eller ange denna kod manuellt:
                        </p>
                        <div className="flex items-center justify-center gap-2">
                          <code className="px-3 py-2 bg-slate-100 rounded text-sm font-mono text-slate-700">
                            {totpData.secret}
                          </code>
                          <button
                            onClick={copySecret}
                            className="p-2 hover:bg-slate-100 rounded transition-colors"
                            title="Kopiera"
                          >
                            {copied ? (
                              <Check size={16} className="text-green-600" />
                            ) : (
                              <Copy size={16} className="text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <Warning size={16} className="text-amber-600 mt-0.5" />
                        <p className="text-xs text-amber-800">
                          Spara den hemliga nyckeln på ett säkert ställe. Du behöver den 
                          om du tappar bort din telefon.
                        </p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => setStep('verify')}
                      className="w-full"
                    >
                      Jag har skannat koden
                    </Button>
                  </div>
                )}
                
                {step === 'verify' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Ange den 6-siffriga koden från din autentiseringsapp för att verifiera
                    </p>
                    
                    <div className="space-y-2">
                      <Label>Verifieringskod</Label>
                      <Input
                        type="text"
                        value={verifyCode}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="text-center font-mono text-2xl tracking-widest"
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setStep('qr')}
                        className="flex-1"
                      >
                        Tillbaka
                      </Button>
                      <Button
                        onClick={handleVerifySetup}
                        disabled={verifyCode.length !== 6 || loading}
                        className="flex-1"
                      >
                        {loading ? 'Verifierar...' : 'Aktivera'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
